import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Landing page for the approve/reject links in a Human Approval email.
//
// Deliberately NOT authenticated: the approver is often a colleague who has no
// account here. The signed token in the link is the credential, and the API
// verifies it on both the read and the write.
//
// Deliberately NOT resolved on page load either. Corporate mail scanners and
// link previewers fetch every URL in an incoming email — if arriving here
// decided the approval, a security appliance would approve things on the
// recipient's behalf. Nothing is decided until a human presses the button.

const CARD = "w-full max-w-lg bg-white dark:bg-[#23263a] rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8";

function Shell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#181d33] px-4 py-10">
            <div className={CARD}>{children}</div>
        </div>
    );
}

function Message({ icon, title, body, tone = "gray" }) {
    const toneClass = {
        green: "text-green-600 dark:text-green-400",
        red: "text-red-600 dark:text-red-400",
        amber: "text-amber-600 dark:text-amber-400",
        gray: "text-gray-600 dark:text-gray-300",
    }[tone];
    return (
        <div className="text-center">
            <div className="text-4xl mb-3">{icon}</div>
            <h1 className={`text-xl font-bold mb-2 ${toneClass}`}>{title}</h1>
            {body && <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{body}</p>}
        </div>
    );
}

export default function ApprovalAction() {
    const { approvalId } = useParams();
    const [params] = useSearchParams();
    const token = params.get("token");

    const [state, setState] = useState({ status: "loading" });
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        if (!token) {
            setState({ status: "error", title: "This link is incomplete", body: "It's missing its security token. Open the original approval email and click the button there again." });
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/approvals/${approvalId}?token=${encodeURIComponent(token)}`);
            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                setState({ status: "ready", approval: data.approval, action: data.tokenAction });
                return;
            }
            if (res.status === 401) {
                setState({ status: "error", title: "This link has expired", body: "Approval links are valid for a limited time. Ask for the request to be sent again." });
            } else if (res.status === 404) {
                setState({ status: "error", title: "Request not found", body: "This approval no longer exists — the workflow may have been deleted." });
            } else {
                setState({ status: "error", title: "Something went wrong", body: data.error || "Please try again in a moment." });
            }
        } catch (e) {
            setState({ status: "error", title: "Couldn't reach the server", body: "Check your connection and try again." });
        }
    }, [approvalId, token]);

    useEffect(() => { load(); }, [load]);

    const decide = async () => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/approvals/${approvalId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, action: state.action, comment }),
            });
            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                setState(s => ({ ...s, status: "done" }));
            } else if (res.status === 409) {
                setState({ status: "error", title: "Already decided", body: data.error || "Someone has already responded to this request.", tone: "amber" });
            } else if (res.status === 410) {
                setState({ status: "error", title: "This request expired", body: "The workflow already moved on according to its timeout setting.", tone: "amber" });
            } else if (res.status === 401) {
                setState({ status: "error", title: "This link has expired", body: "Approval links are valid for a limited time. Ask for the request to be sent again." });
            } else {
                setState({ status: "error", title: "Couldn't record your decision", body: data.error || "Please try again." });
            }
        } catch (e) {
            setState({ status: "error", title: "Couldn't reach the server", body: "Your decision was not recorded. Check your connection and try again." });
        } finally {
            setSubmitting(false);
        }
    };

    if (state.status === "loading") {
        return <Shell><Message icon="⏳" title="Loading request…" /></Shell>;
    }

    if (state.status === "error") {
        return <Shell><Message icon="⚠️" title={state.title} body={state.body} tone={state.tone || "red"} /></Shell>;
    }

    if (state.status === "done") {
        const approved = state.action === "approve";
        return (
            <Shell>
                <Message
                    icon={approved ? "✅" : "🚫"}
                    title={approved ? "Approved" : "Rejected"}
                    tone={approved ? "green" : "red"}
                    body={approved
                        ? "Thanks — the workflow has resumed and will carry on from here."
                        : "Thanks — the workflow has been told not to proceed."}
                />
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">You can close this tab.</p>
            </Shell>
        );
    }

    // status === "ready"
    const { approval, action } = state;
    const approving = action === "approve";
    const preview = approval?.previewData && typeof approval.previewData === "object" ? Object.entries(approval.previewData) : [];
    const alreadyDecided = approval?.status && approval.status !== "pending";
    const expired = approval?.expiresAt && new Date(approval.expiresAt) < new Date();

    if (alreadyDecided) {
        return (
            <Shell>
                <Message
                    icon="ℹ️"
                    tone="amber"
                    title={`Already ${approval.status}`}
                    body={`This request was ${approval.status}${approval.decidedAt ? ` on ${new Date(approval.decidedAt).toLocaleString()}` : ""}. No further action is needed.`}
                />
            </Shell>
        );
    }

    if (expired) {
        return (
            <Shell>
                <Message icon="⌛" tone="amber" title="This request expired"
                    body="The workflow already moved on according to its timeout setting." />
            </Shell>
        );
    }

    return (
        <Shell>
            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-2">Approval required</p>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{approval?.prompt}</h1>

            {preview.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mb-5">
                    <table className="w-full text-sm">
                        <tbody>
                            {preview.map(([k, v], i) => (
                                <tr key={k} className={i % 2 ? "bg-gray-50 dark:bg-[#1e2035]" : ""}>
                                    <td className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 align-top w-1/3 break-words">{k}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 break-words">{String(v)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {approval?.expiresAt && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Expires {new Date(approval.expiresAt).toLocaleString()}.
                </p>
            )}

            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Comment <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </label>
            <textarea
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder={approving ? "Anything the team should know…" : "Why are you rejecting this?"}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-[#181d33] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-5"
            />

            <button
                onClick={decide}
                disabled={submitting}
                className={`w-full py-3 rounded-xl text-white font-semibold transition-colors disabled:opacity-60 ${
                    approving ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}
            >
                {submitting ? "Recording…" : approving ? "✓ Confirm approval" : "✕ Confirm rejection"}
            </button>

            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-3">
                Nothing happens until you press the button above.
            </p>
        </Shell>
    );
}
